import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CreditCard, Lock, Plus, ReceiptText, Wallet } from "lucide-react";
import { api } from "@/shared/api/client";
import { fmtRubFull } from "@/shared/lib/format";
import { Skeleton } from "@/shared/ui";
import { TopUpModal } from "./TopUpModal";

type Transaction = {
  id: number;
  amount: number;
  method: string;
  balance_after: number;
  created_at: string;
};

type BillingData = {
  balance_rub: number;
  frozen_rub: number;
  transactions: Transaction[];
};

function useBilling() {
  return useQuery({
    queryKey: ["billing"],
    queryFn: () => api<BillingData>("/api/v1/account/topup-history"),
  });
}

const METHOD_LABELS: Record<string, string> = {
  card:     "Банковская карта",
  transfer: "Расчётный счёт",
  tinkoff:  "Tinkoff Business",
};

function fmtDateTime(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const date = d.toLocaleDateString("ru-RU", { day: "2-digit", month: "long", year: "numeric" });
  const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  return { date, time };
}

export function BillingPage() {
  const { data, isLoading } = useBilling();
  const [topUpOpen, setTopUpOpen] = useState(false);

  const total = data?.transactions.reduce((s, t) => s + t.amount, 0) ?? 0;

  return (
    <div className="max-w-[800px] mx-auto px-6 py-8">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-display font-extrabold text-[28px] tracking-tight">Баланс и платежи</h1>
          <p className="text-ink-40 text-[13.5px] mt-1">История пополнений рекламного бюджета</p>
        </div>
        <button
          onClick={() => setTopUpOpen(true)}
          className="inline-flex items-center gap-1.5 h-10 px-5 rounded-pill bg-lime text-[var(--fg-on-accent)] font-display font-bold text-[13.5px] hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]"
        >
          <Plus size={14} strokeWidth={3} /> Пополнить
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={<Wallet size={16} className="text-ink-60" />}
          label="Доступно"
          value={isLoading ? null : fmtRubFull(data?.balance_rub ?? 0)}
          accent
        />
        <StatCard
          icon={<Lock size={16} className="text-ink-60" />}
          label="Заморожено под РК"
          value={isLoading ? null : fmtRubFull(data?.frozen_rub ?? 0)}
        />
        <StatCard
          icon={<ReceiptText size={16} className="text-ink-60" />}
          label="Всего пополнено"
          value={isLoading ? null : fmtRubFull(total)}
        />
        <StatCard
          icon={<CreditCard size={16} className="text-ink-60" />}
          label="Пополнений"
          value={isLoading ? null : String(data?.transactions.length ?? 0)}
        />
      </div>

      {/* History table */}
      <div className="bg-white rounded-xl border border-ink-12 overflow-hidden">
        <div className="px-5 py-4 border-b border-ink-12">
          <span className="font-display font-bold text-[15px]">История операций</span>
        </div>

        {isLoading ? (
          <div className="p-5 flex flex-col gap-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-lg" />)}
          </div>
        ) : !data?.transactions.length ? (
          <div className="py-16 text-center">
            <Wallet size={32} strokeWidth={1.5} className="text-ink-12 mx-auto mb-3" />
            <div className="font-display font-semibold text-ink-60">Пополнений пока нет</div>
            <div className="text-ink-40 text-[12.5px] mt-1">Нажмите «Пополнить», чтобы добавить средства</div>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-wider text-ink-40 border-b border-ink-12">
                {["Дата", "Сумма", "Способ оплаты", "Баланс после"].map((h, i) => (
                  <th key={i} className="text-left px-5 py-2.5 font-display font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.transactions.map((tx) => {
                const { date, time } = fmtDateTime(tx.created_at);
                return (
                  <tr key={tx.id} className="border-b border-ink-12/50 last:border-0 hover:bg-ink-6/50">
                    <td className="px-5 py-3.5">
                      <div className="font-display font-semibold">{date}</div>
                      <div className="text-[11px] text-ink-40 font-mono">{time}</div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="font-mono font-bold text-success text-[14px]">
                        +{fmtRubFull(tx.amount)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-ink-60">
                      {METHOD_LABELS[tx.method] ?? tx.method}
                    </td>
                    <td className="px-5 py-3.5 font-mono text-ink-40">
                      {fmtRubFull(tx.balance_after)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <TopUpModal open={topUpOpen} onClose={() => setTopUpOpen(false)} />
    </div>
  );
}

function StatCard({ icon, label, value, accent }: {
  icon: React.ReactNode; label: string; value: string | null; accent?: boolean;
}) {
  return (
    <div className="bg-white rounded-xl border border-ink-12 px-5 py-4">
      <div className="flex items-center gap-2 mb-3 text-ink-40">
        {icon}
        <span className="text-[11px] font-display font-semibold uppercase tracking-wider">{label}</span>
      </div>
      {value === null ? (
        <Skeleton className="h-8 w-32 rounded" />
      ) : (
        <div className={`font-display font-extrabold text-[22px] tracking-tight tabular-nums ${accent ? "text-ink" : ""}`}>
          {value}
        </div>
      )}
    </div>
  );
}
